// src/components/modules/auth/SignInForm.tsx
import {
  faKey,
  faUser,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { loginUser } from 'store/authThunks';

const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);

    try {
      const resultAction = await dispatch(
        loginUser({ email: trimmedEmail, password: trimmedPassword })
      );

      if (loginUser.fulfilled.match(resultAction)) {
        const user = resultAction.payload;
        if (rememberMe) {
          localStorage.setItem('auth', JSON.stringify(user));
        } else {
          sessionStorage.setItem('auth', JSON.stringify(user));
        }
        navigate('/');
      } else {
        setError(
          (resultAction.payload as string) ||
            'Login failed. Please check your credentials.'
        );
      }
    } catch {
      setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign In</h3>
        <p className="text-body-tertiary">Get access to your account</p>
      </div>

      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="email">Email address</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="email"
            type="email"
            className="form-icon-input"
            placeholder="name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <FontAwesomeIcon icon={faUser} className="text-body fs-9 form-icon" />
        </div>
      </Form.Group>

      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="password">Password</Form.Label>
        <div className="form-icon-container position-relative">
          <Form.Control
            id="password"
            type={showPassword ? 'text' : 'password'}
            className="form-icon-input"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={{ paddingRight: '2.5rem' }}
          />
          <FontAwesomeIcon icon={faKey} className="text-body fs-9 form-icon" />
          <FontAwesomeIcon
            icon={showPassword ? faEyeSlash : faEye}
            className="text-body fs-9 position-absolute"
            style={{
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              zIndex: 10
            }}
            onClick={() => setShowPassword(!showPassword)}
          />
        </div>
      </Form.Group>

      <Row className="flex-between-center mb-7">
        <Col xs="auto">
          <Form.Check type="checkbox" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember-me"
              id="remember-me"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
            />
            <Form.Check.Label htmlFor="remember-me" className="mb-0">
              Remember me
            </Form.Check.Label>
          </Form.Check>
        </Col>
        <Col xs="auto">
          <Link to="/forgot-password" className="fs-9 fw-semibold">
            Forgot Password?
          </Link>
        </Col>
      </Row>

      {error && <p className="text-danger text-center mb-3">{error}</p>}

      <Button
        type="submit"
        variant="primary"
        className="w-100 mb-3"
        disabled={loading}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
    </Form>
  );
};

export default SignInForm;
